import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { useCachedState, useRefreshVersion } from './store';
import { Badge, Empty, ErrorState, Metric, Select, ago, date, shortNumber, useMe } from './ui';
import { Flag, canOpenAccounts, openCustomerAccount } from './Users';

const PAGE_SIZE = 25;
const EMPTY_FILTERS = { search: '', rating: '', sentiment: '', withComment: false };

export const Stars = ({ value }) => value == null ? null : <span className="stars" title={`${value} / 5`}>{'★'.repeat(Math.round(value))}<i>{'★'.repeat(5 - Math.round(value))}</i></span>;

const sentimentOf = (rating) => (rating >= 4 ? 'good' : rating === 3 ? 'warn' : 'danger');

/**
 * Every answer to the "how did that go?" prompt shown after a render.
 *
 * Its own page rather than a side panel on Customers: a list of eight recent
 * answers could not be searched, filtered to the unhappy ones, or followed
 * back to the video and the account — and it took a third of the width the
 * customer table needed. The headline numbers (average, distribution) are
 * over every answer ever given; the filters only narrow the list below.
 */
export function Feedback({ onOpenVideos }) {
  const me = useMe();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const defaultQuery = new URLSearchParams({ limit: PAGE_SIZE, offset: 0 }).toString();
  const [data, setData] = useCachedState(`/feedback?${defaultQuery}`);
  const [loading, setLoading] = useState(!data);
  const [error, setError] = useState('');
  const refreshVersion = useRefreshVersion();

  // The search box applies a beat after the last keystroke; everything else
  // applies at once.
  useEffect(() => {
    const timer = setTimeout(() => { setApplied(filters); setPage(0); }, filters.search === applied.search ? 0 : 350);
    return () => clearTimeout(timer);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => {
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (applied.search.trim()) params.search = applied.search.trim();
    if (applied.rating) params.rating = applied.rating;
    if (applied.sentiment) params.sentiment = applied.sentiment;
    if (applied.withComment) params.withComment = 'true';
    return new URLSearchParams(params).toString();
  }, [applied, page]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    api(`/feedback?${query}`).then((next) => { if (!cancelled) setData(next); })
      .catch((caught) => { if (!cancelled) setError(caught.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query, refreshVersion]);

  const set = (key) => (event) => setFilters((f) => ({ ...f, [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value }));
  const s = data?.stats;
  const total = data?.page?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const answers = Number(s?.total ?? 0);
  const positiveShare = answers ? Math.round((Number(s.positive) / answers) * 100) : null;
  const distribution = data?.distribution ?? [];
  const maxCount = Math.max(1, ...distribution.map((d) => d.count));
  const activeCount = ['search', 'rating', 'sentiment', 'withComment'].filter((k) => applied[k]).length;

  return <>
    <section className="page-title"><div><div className="eyebrow">Feedback</div><h1>Feedback</h1><p>What customers said after a render, and which videos they said it about.</p></div>
      {activeCount > 0 && <button className="subtle" onClick={() => setFilters(EMPTY_FILTERS)}>Clear {activeCount} filter{activeCount > 1 ? 's' : ''}</button>}</section>

    <section className={`metric-grid ${loading && !data ? 'is-loading' : ''}`}>
      <Metric label="Average rating" value={s?.avg_rating ? `${Number(s.avg_rating).toFixed(1)} ★` : '—'} detail={answers ? `${shortNumber(answers)} answer${answers === 1 ? '' : 's'} · ${s.last_7d ?? 0} this week` : 'No answers yet'} />
      <Metric label="Happy" value={positiveShare == null ? '—' : `${positiveShare}%`} detail={`${s?.positive ?? 0} rated 4 or 5`} />
      <Metric label="Unhappy" value={shortNumber(s?.negative)} detail="Rated 1 or 2 — worth a reply" alert={Number(s?.negative) > 0} />
      <Metric label="With a comment" value={shortNumber(s?.with_comment)} detail={answers ? `${Math.round((Number(s.with_comment) / answers) * 100)}% wrote something` : 'Ratings alone say less'} />
    </section>

    <section className="toolbar">
      <input value={filters.search} onChange={set('search')} placeholder="Search comments, customer, or video title" />
      <Select value={filters.sentiment} onChange={set('sentiment')} label="Answer" options={[['', 'All answers'], ['positive', 'Happy (4–5)'], ['neutral', 'Neutral (3)'], ['negative', 'Unhappy (1–2)']]} />
      <Select value={filters.rating} onChange={set('rating')} label="Rating" options={[['', 'Any rating'], ...[5, 4, 3, 2, 1].map((r) => [String(r), `${'★'.repeat(r)} ${r}`])]} />
      <label className="check-inline"><input type="checkbox" checked={filters.withComment} onChange={set('withComment')} /> With comment only</label>
    </section>

    {error && <ErrorState message={error} retry={() => setApplied({ ...applied })} />}

    <div className="feedback-layout">
      <article className={`panel table-panel ${loading ? 'is-loading' : ''}`}>
        <div className="panel-heading"><div><span className="eyebrow">ANSWERS</span><h2>{total} answer{total === 1 ? '' : 's'}</h2></div>
          <div className="pager"><button className="subtle" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Prev</button><span>Page {page + 1} of {pages}</span><button className="subtle" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next →</button></div></div>
        <div className="table-wrap"><table className="feedback-table"><thead><tr><th>Rating</th><th>Comment</th><th>Customer</th><th>Video</th><th>When</th>{canOpenAccounts(me) && <th />}</tr></thead><tbody>
          {data?.items?.length ? data.items.map((f) => <tr key={f.id}>
            <td><Stars value={f.rating} /><small className="plain"><Badge tone={sentimentOf(f.rating)}>{f.rating} / 5</Badge></small></td>
            <td className="comment-cell">{f.comment ? <p>{f.comment}</p> : <span className="unassigned">No comment</span>}</td>
            <td><b>{f.user_name || f.user_email}</b><small>{f.user_name ? f.user_email : ''}</small>
              {f.user_country && <small className="plain country-line"><Flag code={f.user_country} /></small>}</td>
            <td>{f.project_id
              ? <><button className="text-button video-link" title="Open this video in the pipeline explorer" onClick={() => onOpenVideos(f.project_id)}>{f.project_title || 'Untitled project'} →</button>
                {f.project_expired_at && <small className="plain">Files deleted</small>}</>
              : <span className="unassigned">Video removed</span>}</td>
            <td><span title={date(f.created_at)}>{ago(f.created_at)}</span></td>
            {canOpenAccounts(me) && <td className="row-actions"><button className="subtle" onClick={() => openCustomerAccount({ id: f.user_id, email: f.user_email })}>Open account ↗</button></td>}
          </tr>) : <tr><td colSpan="6"><Empty title={activeCount ? 'No answers match these filters' : 'No feedback yet'} body={activeCount ? 'Try clearing a filter.' : 'Customers are asked how a render went, at most once a week.'} /></td></tr>}
        </tbody></table></div>
      </article>

      <article className="panel">
        <div className="panel-heading"><div><span className="eyebrow">DISTRIBUTION</span><h2>All answers by rating</h2></div></div>
        <ul className="rating-bars">
          {distribution.map((d) => {
            const share = answers ? Math.round((d.count / answers) * 100) : 0;
            return <li key={d.rating} className={applied.rating === String(d.rating) ? 'is-on' : ''}>
              <button className="text-button" onClick={() => setFilters((f) => ({ ...f, rating: f.rating === String(d.rating) ? '' : String(d.rating) }))} title={`Show only ${d.rating}-star answers`}>
                <span className="rating-label"><Stars value={d.rating} /></span>
                <span className="rating-track"><i className={sentimentOf(d.rating)} style={{ width: `${(d.count / maxCount) * 100}%` }} /></span>
                <span className="rating-count"><b>{d.count}</b><small>{share}%</small></span>
              </button>
            </li>;
          })}
        </ul>
      </article>
    </div>
  </>;
}
